import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useActiveContextReady } from './useActiveContextReady';
import { config } from '../config';

// Fields match `views::firewall_rules` on the backend exactly.
// Phase 1a: rules are parsed from the device's captured config for vendors that
// have a parser. `mock` is kept in the contract and is now always false.

export interface SnapshotSource {
  snapshotId: string;
  capturedAt: string;
  format: string;
  checksum: string;
}

export interface RuleCoverage {
  // Every line in the capture. Context only — never the denominator.
  totalLines: number;
  // Lines matching the vendor's filter-construct grammar. This is what
  // coverage is measured against: the question is whether we understood the
  // filter config, not whether we understood the whole device config.
  inScope: number;
  unaccountedLines: number;
}

export interface RuleEndpoint {
  text: string;
  objectName: string | null;
}

export interface RuleExtension {
  vendor: string;
  key: string;
  value: string;
}

export interface RuleLint {
  kind: string; // "shadowed" | "redundant" | "unbound" | …
  message: string;
}

export interface FirewallRule {
  seq: number;
  action: string; // "permit" | "deny" | "reject" | "jump" | …
  protocol: string;
  source: RuleEndpoint;
  destination: RuleEndpoint;
  service: string | null;
  hits: number | null;
  implicit: boolean;
  // False for a rule the device carries but has switched off. It renders in
  // position and never matches; hiding it would renumber what the operator
  // sees on the device.
  enabled: boolean;
  extensions: RuleExtension[];
  lints: RuleLint[];
  /** What a NAT rule rewrites the packet to, per column. Absent for a filter rule. */
  translation?: {
    source: string | null;
    destination: string | null;
    service: string | null;
  };
  provenance: {
    origin: string;
    nativeLine: number | null;
    // Set when the rule was reconstructed from a shorthand the device compiles
    // (EdgeOS port-forward) rather than read from the config.
    derivedFrom: string | null;
  };
}

export interface FirewallRuleSet {
  id: string;
  name: string;
  family: string; // "v4" | "v6" — part of identity, not a predicate
  hook: string;
  scope: string | null;
  // The same scope as two lists, when the set is bound to every pairing of
  // them — a zone pair, which can be 49 interface pairs. null: any interface.
  interfaces: { from: string[] | null; to: string[] | null } | null;
  stateful: boolean;
  defaultAction: string;
  // True when the platform default was assumed rather than read.
  defaultAssumed: boolean;
  // Lints about the set as a whole — `unbound` lives here, because being
  // referenced by nothing is a property of the set, not of any one rule.
  lints: RuleLint[];
  rules: FirewallRule[];
}

export interface FirewallRulesResponse {
  mock: boolean;
  deviceId: string;
  // "modeled" | "invisible" (unknown — never rendered as absence) | "none"
  enforcement: string;
  source: SnapshotSource | null;
  coverage: RuleCoverage;
  // Why there are no rules, when there are none — "no parser for this vendor"
  // and "nothing captured yet" are different states and must not both render
  // as an empty list.
  reason: string | null;
  ruleSets: FirewallRuleSet[];
}

export function useFirewallRules(deviceId: string | undefined) {
  const contextReady = useActiveContextReady();
  return useQuery<FirewallRulesResponse>({
    queryKey: ['firewallRules', deviceId],
    queryFn: async () => {
      if (!deviceId) throw new Error('Device ID is required');
      const response = await axios.get<FirewallRulesResponse>(
        `${config.backendApi}/api/devices/${deviceId}/firewall-rules`,
        { withCredentials: true }
      );
      return response.data;
    },
    enabled: !!deviceId && contextReady,
  });
}
